import type { FieldTree, SchemaPathTree } from '@angular/forms/signals';
import { readonly, validateStandardSchema } from '@angular/forms/signals';

import type { DefaultSection, SectionEntity, SpecificSection, ZeroSection } from '@sites/entity-core';
import { sectionDefaultV0Schema, sectionSpecificV0Schema, sectionZeroV0Schema } from '@sites/entity-core';

import type { BaseEntityFormOptions } from '../../core/create-base-entity-form';
import { createBaseEntityForm } from '../../core/create-base-entity-form';
import { mergeAccessors } from '../../core/merge-accessors';
import { withSpacingSides } from '../../mixins/spacing/spacing-updaters';

export type SectionFormModel = SectionEntity;
export type SectionSpecificProps = SpecificSection['props'];
export type SectionDefaultProps = DefaultSection['props'];
export type SectionSpacingValue = SectionSpecificProps['padding']['value'];

export type SectionDefaultForm = ReturnType<typeof createSectionDefaultForm>;
export type SectionSpecificForm = ReturnType<typeof createSectionSpecificForm>;
export type SectionZeroForm = ReturnType<typeof createSectionZeroForm>;
export type SectionForm = SectionDefaultForm | SectionSpecificForm | SectionZeroForm;

type SectionVariant = 'default' | 'specific' | 'zero';

/**
 * The slices of a row the two shared accessor groups touch — the envelope all
 * three subtypes have, and the gap every subtype but zero does.
 *
 * Concrete, not generic. `FieldTree<T>` is a conditional type, so through a
 * bounded generic TypeScript defers it and `_raw.props` stops being navigable;
 * naming the shared shape is what lets the getters below hand back a real leaf
 * type instead of `any`. The narrowing cast at each call site is the price, and
 * it buys a typed accessor for every consumer.
 */
type SectionEnvelopeLike = Pick<DefaultSection, 'children'>;
type SectionColumnGapLike = { props: Pick<SectionDefaultProps, 'columnGap'> };

type EnvelopeBase = { _raw: FieldTree<SectionEnvelopeLike> };
type ColumnGapBase = { _raw: FieldTree<SectionColumnGapLike> };

/**
 * `id`, `parentId` and the version tags are infrastructure: the editor never
 * lets the user mutate them, and the host's move/copy actions own the parent
 * link, so they are read-only on the schema path.
 */
/** Same story as the accessors: the schema path is a conditional type too. */
const configureSectionReadonly = (schemaPath: SchemaPathTree<SectionEntity>): void => {
    readonly(schemaPath.id);
    readonly(schemaPath.parentId);
    readonly(schemaPath.entityType);
    readonly(schemaPath.entitySubType);
    readonly(schemaPath.version);
};

/**
 * Envelope accessors all three subtypes share.
 *
 * `parentId` is deliberately absent — see the column form for why: the base
 * already exposes it as a read-only signal, and re-declaring it as a FieldTree
 * gave one name two meanings across the five entity forms.
 */
const createCommonAccessors = (base: EnvelopeBase) => {
    const { _raw } = base;

    return {
        /**
         * Column ids — the host owns adds/removes; the form only reorders.
         *
         * `unknown[]`, not `string[]`: the envelope holds ids in the store but
         * whole column objects while the structure adapter still has the
         * denormalized tree, which is what `sectionBaseSchema` says.
         */
        get children() {
            return _raw.children;
        },
        setChildren: (value: unknown[]) => {
            _raw.children().value.set(value);
        },
        updateChildren: (updater: (current: unknown[]) => unknown[]) => {
            _raw.children().value.update(updater);
        },
    };
};

/**
 * The gap the row puts between its columns — every subtype but zero has one.
 *
 * Flat, like everything else on a row: a row is not per-breakpoint, it inherits
 * the breakpoint of the block it sits in.
 */
const createColumnGapAccessors = (base: ColumnGapBase) => {
    const { _raw } = base;

    return {
        get columnGap() {
            return _raw.props.columnGap.value;
        },
        setColumnGap: (value: number) => {
            _raw.props.columnGap.value().value.set(value);
        },
    };
};

export function createSectionDefaultForm(options?: BaseEntityFormOptions<DefaultSection>) {
    const base = createBaseEntityForm<DefaultSection>((schemaPath) => {
        configureSectionReadonly(schemaPath);
        validateStandardSchema(schemaPath, sectionDefaultV0Schema);
    }, options);

    const { _raw } = base;

    // `mergeAccessors`, not object spread: spreading would call the getters while
    // the model is still the empty seed.
    return mergeAccessors(
        mergeAccessors(
            mergeAccessors({ ...base }, createCommonAccessors(base as EnvelopeBase)),
            createColumnGapAccessors(base as ColumnGapBase)
        ),
        {
            get isFlexibleWidths() {
                return _raw.props.isFlexibleWidths;
            },
            setIsFlexibleWidths: (value: boolean) => {
                _raw.props.isFlexibleWidths().value.set(value);
            },
        }
    );
}

export function createSectionZeroForm(options?: BaseEntityFormOptions<ZeroSection>) {
    const base = createBaseEntityForm<ZeroSection>((schemaPath) => {
        configureSectionReadonly(schemaPath);
        validateStandardSchema(schemaPath, sectionZeroV0Schema);
    }, options);

    // A zero row carries no settings of its own — only the envelope.
    return mergeAccessors({ ...base }, createCommonAccessors(base as EnvelopeBase));
}

export function createSectionSpecificForm(options?: BaseEntityFormOptions<SpecificSection>) {
    const base = createBaseEntityForm<SpecificSection>((schemaPath) => {
        configureSectionReadonly(schemaPath);
        validateStandardSchema(schemaPath, sectionSpecificV0Schema);
    }, options);

    const { _raw } = base;
    const background = () => _raw.props.background;

    return mergeAccessors(
        mergeAccessors(
            mergeAccessors({ ...base }, createCommonAccessors(base as EnvelopeBase)),
            createColumnGapAccessors(base as ColumnGapBase)
        ),
        {
            // Every accessor below is a single FieldTree leaf, so a host binding them
            // one at a time only recomputes the one that changed.
            get verticalPosition() {
                return _raw.props.verticalPosition;
            },
            setVerticalPosition: (value: SectionSpecificProps['verticalPosition']) => {
                _raw.props.verticalPosition().value.set(value);
            },

            get showLeftColumn() {
                return _raw.props.slots.left;
            },
            setShowLeftColumn: (value: boolean) => {
                _raw.props.slots.left().value.set(value);
            },
            get showCenterColumn() {
                return _raw.props.slots.center;
            },
            setShowCenterColumn: (value: boolean) => {
                _raw.props.slots.center().value.set(value);
            },
            get showRightColumn() {
                return _raw.props.slots.right;
            },
            setShowRightColumn: (value: boolean) => {
                _raw.props.slots.right().value.set(value);
            },

            get backgroundColor() {
                return background().color;
            },
            setBackgroundColor: (value: SectionSpecificProps['background']['color']) => {
                background().color().value.set(value);
            },
            get backgroundImage() {
                return background().image;
            },
            setBackgroundImage: (value: SectionSpecificProps['background']['image']) => {
                background().image().value.set(value);
            },
            // An empty string is stored as `null`, the one spelling of unset.
            get backgroundSize() {
                return background().size;
            },
            setBackgroundSize: (value: string | null) => {
                background()
                    .size()
                    .value.set(value || null);
            },
            get backgroundPosition() {
                return background().position;
            },
            setBackgroundPosition: (value: string | null) => {
                background()
                    .position()
                    .value.set(value || null);
            },
            get backgroundRepeat() {
                return background().repeat;
            },
            setBackgroundRepeat: (value: string | null) => {
                background()
                    .repeat()
                    .value.set(value || null);
            },

            get padding() {
                return _raw.props.padding;
            },
            /** The four sides live in one leaf, so a single-side edit merges into it. */
            setPadding: (patch: Partial<SectionSpecificProps['padding']['value']>) => {
                _raw.props.padding().value.update(withSpacingSides(patch));
            },

            /** Vertical margin around each slot — see the schema; nothing edits it. */
            get slotVerticalMargin() {
                return _raw.props.slotVerticalMargin.value;
            },
            setSlotVerticalMargin: (value: number) => {
                _raw.props.slotVerticalMargin.value().value.set(value);
            },
        }
    );
}

/**
 * Picks the factory from the entity's own subtype. The three variants differ in
 * shape, so the schema has to be chosen at construction — a header row handed to
 * the default factory would validate against a schema that knows nothing about
 * its slots or background.
 */
export function createSectionForm(options?: BaseEntityFormOptions<SectionEntity>): SectionForm {
    const entity =
        (options as { initialData?: SectionEntity })?.initialData ?? (options?.entity?.() as SectionEntity | undefined);
    const variant: SectionVariant = entity?.entitySubType ?? 'default';

    if (variant === 'specific') {
        return createSectionSpecificForm(options as BaseEntityFormOptions<SpecificSection>);
    }

    if (variant === 'zero') {
        return createSectionZeroForm(options as BaseEntityFormOptions<ZeroSection>);
    }

    return createSectionDefaultForm(options as BaseEntityFormOptions<DefaultSection>);
}
